"use client";

import React, { ReactNode, useCallback, useEffect, useState } from "react";
import ClassNames from "embla-carousel-class-names";
import useEmblaCarousel from "embla-carousel-react";
import { EmblaCarouselType } from "embla-carousel";
import { FeedEvents, trackEvent } from "@/analytics";
import CarouselLeftButton from "@/components/common/carousels/CarouselLeftButton";
import CarouselRightButton from "@/components/common/carousels/CarouselRightButton";

export type SlideData = {
  Component: ReactNode;
  id: string;
};

type Props = {
  slides: SlideData[];
};

const AdUnitCarousel = ({ slides }: Props) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: false, dragFree: true },
    [ClassNames()]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState<boolean>(false);
  const [canScrollNext, setCanScrollNext] = useState<boolean>(false);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(
    (emblaApi: EmblaCarouselType) => {
      const newIndex = emblaApi.selectedScrollSnap();

      // Track analytics based on direction - captures ALL scroll methods
      if (newIndex > selectedIndex) {
        trackEvent(FeedEvents.CLICKED_NEXT_FEED_AD_UNIT_CAROUSEL);
      } else if (newIndex < selectedIndex) {
        trackEvent(FeedEvents.CLICKED_PREVIOUS_FEED_AD_UNIT_CAROUSEL);
      }

      setSelectedIndex(newIndex);
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    },
    [selectedIndex]
  );

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);
  }, [emblaApi, onSelect]);

  if (!slides || slides.length === 0) return null;

  return (
    <div className="relative overflow-hidden">
      <CarouselLeftButton
        onClick={onPrevButtonClick}
        isDisabled={!canScrollPrev}
      />
      <CarouselRightButton
        onClick={onNextButtonClick}
        isDisabled={!canScrollNext}
      />

      <div className="relative overflow-hidden" ref={emblaRef}>
        <div className="flex flex-row mx-4 touch-pan-y backface-hidden gap-y-8 gap-x-6 md:mr-6">
          {slides
            .filter((slide) => slide !== null)
            .map(({ Component, id }) => (
              <React.Fragment key={id}>{Component}</React.Fragment>
            ))}
        </div>
      </div>
    </div>
  );
};

export default AdUnitCarousel;
